import { Drawer, Box, Typography, IconButton, Chip, useTheme, Divider, CircularProgress } from '@mui/material';
import { IoClose } from 'react-icons/io5';
import { formatDollars, formatRevenue } from 'utils/helperFunctions';
import DataTable from 'components/Common/DataTable';
import BillingHistoryRowDetails from 'components/Common/BillingHistoryRowDetails';
import { ColumnDef } from '@tanstack/react-table';
import { useGetAgencyMontlyRevenue, useFetchAgencyBusinessInvoice } from 'apis/agency/revenue/hook';
import { MdOutlineFileDownload, MdKeyboardArrowDown, MdKeyboardArrowUp } from 'react-icons/md';

interface Props {
  open: boolean;
  onClose: () => void;
  data: any;
}

export default function RevenueDetailsDrawer({ open, onClose, data }: Props) {
  const theme = useTheme();
  const businessId = data?.business_id || data?.id;
  const {
    mutate: fetchInvoice,
    isPending: isFetchingInvoice,
    variables: fetchingInvoiceId
  } = useFetchAgencyBusinessInvoice(businessId);

  const getStatus = (status: string) => {
    switch (status) {
      case 'pending':
      case 'draft':
        return { color: '#777777', bgcolor: '#ECECEC', label: 'Draft' };

      case 'finalized':
      case 'failed':
        return { color: '#7C00B1', bgcolor: '#F5DFFF', label: 'Open' };

      case 'succeeded':
        return { color: '#106520', bgcolor: '#BAE7CC', label: 'Paid' };

      case 'voided':
        return { color: '#FB8703', bgcolor: '#FFF3E5', label: 'Voided' };

      case 'refunded':
        return { color: '#B36B00', bgcolor: '#FFF3DF', label: 'Refunded' };

      default:
        return {
          color: theme.palette.primary.dark,
          bgcolor: theme.palette.primary.light,
          label: status
        };
    }
  };

  const { data: monthlyRevenueData, isLoading } = useGetAgencyMontlyRevenue(
    businessId,
    {},
    { enabled: !!businessId && open }
  );

  if (!data) return null;

  const columns: ColumnDef<any>[] = [
    {
      header: 'MONTH',
      accessorKey: 'month',
      cell: ({ row }) => (
        <Box display='flex' alignItems='center' gap={0.5}>
          <IconButton size='small' onClick={row.getToggleExpandedHandler()} sx={{ p: 0.25 }}>
            {row.getIsExpanded() ? <MdKeyboardArrowUp size={18} /> : <MdKeyboardArrowDown size={18} />}
          </IconButton>
          <Typography>{row.original.month}</Typography>
        </Box>
      ),
      size: 120
    },
    {
      header: 'SUBTOTAL',
      accessorKey: 'subtotal',
      cell: ({ getValue }) => formatDollars(getValue<number>()),
      size: 100
    },
    {
      header: 'TAX',
      accessorKey: 'tax',
      cell: ({ getValue }) => formatDollars(getValue<number>()),
      size: 100
    },
    {
      header: 'DISCOUNT',
      accessorKey: 'discount_amount',
      cell: ({ getValue }) => <span style={{ color: 'green' }}>{formatDollars(getValue<number>())}</span>,
      size: 100
    },
    {
      header: 'TOTAL (INCL. TAX)',
      accessorKey: 'total',
      cell: ({ getValue }) => formatDollars(getValue<number>()),
      size: 120
    },
    {
      header: 'STATUS',
      accessorKey: 'status',
      size: 120,
      cell: ({ row }) =>
        row.original.status ? (
          <Chip
            label={getStatus(row.original.status).label}
            size='small'
            sx={{
              color: getStatus(row.original.status).color,
              bgcolor: getStatus(row.original.status).bgcolor,
              borderRadius: 6,
              px: 1.5,
              fontSize: '12px',
              fontWeight: 500,
              height: 28
            }}
          />
        ) : (
          ''
        )
    },
    {
      header: 'INVOICE',
      size: 40,
      id: 'invoice',
      cell: ({ row }) => {
        const { id, status } = row.original;
        const isRowFetching = isFetchingInvoice && fetchingInvoiceId === id;

        return ['finalized', 'failed', 'voided', 'succeeded', 'refunded'].includes(status) ? (
          <IconButton color='primary' disabled={!id || isRowFetching} onClick={() => id && fetchInvoice(id)}>
            <MdOutlineFileDownload />
          </IconButton>
        ) : (
          ''
        );
      }
    }
  ];

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: { xs: '100%', sm: 520, md: 823 }
        }
      }}
    >
      <Box
        px={3}
        py={2}
        display='flex'
        justifyContent='space-between'
        alignItems='center'
        borderBottom='1px solid #E5E7EB'
      >
        <Typography fontWeight={500} fontSize={20}>
          Billing History
        </Typography>

        <IconButton onClick={onClose}>
          <IoClose />
        </IconButton>
      </Box>

      <Box p={3}>
        <Typography color='text.secondary' mb={2}>
          Monthly usage and billing details for{' '}
          <span style={{ color: theme.palette.primary.main, fontWeight: 500 }}>{data.business_name}</span>
        </Typography>

        <Box
          display='flex'
          justifyContent='space-between'
          alignItems='center'
          p={2}
          borderRadius={2}
          border='1px solid #DCE3F1'
          bgcolor='#F0F6FF'
          mb={3}
        >
          <Box>
            <Typography fontSize={12} color='text.secondary'>
              Package
            </Typography>
            <Typography fontWeight={500}>{data.package || 'No Package'}</Typography>
          </Box>

          <Divider orientation='vertical' flexItem sx={{ borderColor: '#195EFF' }} />

          <Box>
            <Typography fontSize={12} color='text.secondary'>
              All Time Revenue
            </Typography>
            <Typography fontWeight={600}>{formatRevenue(data.all_time_revenue)}</Typography>
          </Box>
        </Box>

        <Box border='1px solid #E5E7EB' borderRadius={2} overflow='hidden'>
          {isLoading ? (
            <Box display='flex' justifyContent='center' p={4}>
              <CircularProgress size={30} />
            </Box>
          ) : (
            <DataTable
              data={monthlyRevenueData?.data || []}
              columns={columns}
              isHeaderSticky={false}
              renderAfterRow={(row) => <BillingHistoryRowDetails invoiceId={row.original.id} isBusinessView={false} />}
            />
          )}
        </Box>
      </Box>
    </Drawer>
  );
}
